import {
  Card,
  CardBody,
  HStack,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
} from "@chakra-ui/react";
import { keepPreviousData } from "@tanstack/react-query";
import { FeeOffsettableExpensesTable } from "features/fees/offsets/FeeOffsettableExpensesTable";
import useOffsetCodeConfigs from "features/fees/offsets/useOffsetCodeConfigs";
import { useFeeChargeEventList } from "gen/endpoints/fee-charge-event/fee-charge-event";
import { useFeeStatsRetrieveSuspense } from "gen/endpoints/fee-stats/fee-stats";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CardSection,
  CardSectionBody,
  CardSectionTitle,
} from "shared/components/card/CardSection";
import { LoadingState } from "shared/components/empty/LoadingState";
import { PageTabBody } from "shared/components/layout/PageTabBody";
import { TableBox } from "shared/components/table/box/TableBox";
import { TableBoxBody } from "shared/components/table/box/TableBoxBody";
import { TableBoxHeader } from "shared/components/table/box/TableBoxHeader";
import { TableBoxTitle } from "shared/components/table/box/TableBoxTitle";
import { DataTable } from "shared/components/table/data/DataTable";
import { DataTableBoxActions } from "shared/components/table/data/DataTableBoxActions";
import { GetLinkInfoForCellFn } from "shared/components/table/data/DataTableTypes";
import { EqualizedViewToggleButton } from "shared/components/toggle/EqualizedViewToggleButton";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { formatMoney } from "shared/utils/MoneyUtils";

export const FeesPostingTab: React.FC = () => {
  const { fundFamilyId } = useRequiredStringParams();
  const { t } = useTranslation(["fees", "common"]);

  const [isEqualizedView, setIsEqualizedView] = useState<boolean>(false);

  // If Fees are posted in the future, we need to include those in this metric
  const { data: statsData } = useFeeStatsRetrieveSuspense(fundFamilyId);

  const { data: feesBreakdownTableData, isFetching } = useFeeChargeEventList(
    fundFamilyId,
    {
      is_equalized_view: isEqualizedView,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );

  const { data: offsetCodeConfigs } = useOffsetCodeConfigs(fundFamilyId);

  const getLinkInfoForCell = useCallback<GetLinkInfoForCellFn>(
    ({ type, columnContext: { column } }) => {
      const baseRoute = rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .fees({});
      if (
        type == "header" &&
        column.id &&
        column.is_header == false &&
        column.metadata?.type === "fee_charge_event_summary" &&
        column.metadata.fee_charge_event_id
      ) {
        return {
          type: "generic",
          variant: "tableColumnHeader",
          to: baseRoute.feeChargeEventPosted({
            feeChargeEventId: column.metadata.fee_charge_event_id,
          }).$,
        };
      }
    },
    [fundFamilyId],
  );

  return (
    <PageTabBody isFullHeight={true}>
      <HStack spacing="24px" mb="24px" alignItems="stretch">
        <Card variant="stat" flex={1}>
          <CardBody>
            <CardSection>
              <CardSectionTitle>{t("fees:inceptionToDate")}</CardSectionTitle>
              <CardSectionBody>
                <StatGroup alignItems="center" justifyItems="center">
                  <Stat size="sm">
                    <StatNumber>
                      {formatMoney(statsData.fees_inception_to_date.fees)}
                    </StatNumber>
                    <StatLabel>{t("fees:fees")}</StatLabel>
                  </Stat>
                  <Stat size="sm">
                    <StatNumber>
                      {formatMoney(statsData.fees_inception_to_date.net_fees)}
                    </StatNumber>
                    <StatLabel>{t("fees:netFees")}</StatLabel>
                  </Stat>
                </StatGroup>
              </CardSectionBody>
            </CardSection>
          </CardBody>
        </Card>
        <Card variant="stat" flex={2}>
          <CardBody>
            <CardSection>
              <CardSectionTitle>
                {t("fees:mostRecentPeriod", {
                  period: statsData.fees_recent_period.recent_period_rich_text,
                })}
              </CardSectionTitle>
              <CardSectionBody>
                <StatGroup>
                  <Stat size="sm">
                    <StatNumber>
                      {formatMoney(statsData.fees_recent_period.fees)}
                    </StatNumber>
                    <StatLabel>{t("fees:fees")}</StatLabel>
                  </Stat>
                  <Stat size="sm">
                    <StatNumber>
                      {formatMoney(statsData.fees_recent_period.net_fees)}
                    </StatNumber>
                    <StatLabel>{t("fees:netFees")}</StatLabel>
                  </Stat>
                  {statsData.fees_basis_details.fee_basis_parts.map(
                    (fee_basis_details) => (
                      <Stat size="sm" key={fee_basis_details.fee_basis_id}>
                        <StatNumber>
                          {formatMoney(fee_basis_details.basis_amount)}
                        </StatNumber>
                        <StatLabel>
                          Basis: {fee_basis_details.fee_basis_name}
                        </StatLabel>
                      </Stat>
                    ),
                  )}
                </StatGroup>
              </CardSectionBody>
            </CardSection>
          </CardBody>
        </Card>
      </HStack>
      <TableBox isFullWidth={true}>
        <TableBoxHeader>
          <TableBoxTitle>{t("fees:historicalFees")}</TableBoxTitle>
          <DataTableBoxActions>
            <EqualizedViewToggleButton
              isEqualizedView={isEqualizedView}
              setIsEqualizedView={setIsEqualizedView}
            />
          </DataTableBoxActions>
        </TableBoxHeader>
        <TableBoxBody>
          {feesBreakdownTableData ? (
            <DataTable
              data={feesBreakdownTableData}
              getLinkInfoForCell={getLinkInfoForCell}
              isLoading={isFetching}
            />
          ) : (
            <LoadingState />
          )}
        </TableBoxBody>
      </TableBox>
      {(offsetCodeConfigs?.length ?? 0) > 0 && (
        <>
          <br />
          <FeeOffsettableExpensesTable fundFamilyId={fundFamilyId} />
        </>
      )}
    </PageTabBody>
  );
};
